import { useState, useEffect, useCallback, useRef } from 'react';
import Axios from 'axios';
import { useAlert } from 'react-alert';
import { ModalHeader, ModalItemSelection } from '../../Layout/ModalElements';
import LoadingModal from '../../Layout/LoadingModal.js';
import { confirmAlert } from 'react-confirm-alert';
import Button from 'react-bootstrap/Button';
import { FaSyncAlt, FaFileExport, FaCloudDownloadAlt } from 'react-icons/fa';
import { BsPlus, BsDash } from 'react-icons/bs';
import { Document, Page } from 'react-pdf';
import FileDownload from 'js-file-download';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import dayjs from 'dayjs';

let CancelToken = Axios.CancelToken;
let source = CancelToken.source();

const availablePageScales = [
  { label: 90, value: 1.0 },
  { label: 100, value: 1.25 },
  { label: 125, value: 1.5 },
  { label: 150, value: 1.75 },
  { label: 175, value: 2.0 },
  { label: 200, value: 2.25 },
  { label: 250, value: 2.75 },
  { label: 300, value: 3.5 },
];

const IntVerFilePreview = ({ scalePreviewInfo, scalePreviewInfoSrc, closeModal }) => {

  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageScale, setPageScale] = useState(3);
  const [data, setData] = useState([]);
  const [currentDataIdx, setCurrentDataIdx] = useState(0);

  const isFirstRender = useRef(true);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  }

  const GetGeneratedVerification = useCallback((records, idx) => {

    setLoading(true);

    const opts = { cancelToken: source.token };
    const url = 'api/downloads/intVer/fixedScale/batch';

    Axios.post(url, { records }, opts)
    .then((res) => {
      setLoading(false);
      if (idx || idx === 0) {
        let newRecords = [...data];
        newRecords[idx] = res.data[0];
        setData(newRecords);
      } else {
        setData(res.data);
      }
      let resErrs = res.data.filter(r => r.err);
      if (resErrs.length) {
        let errResp = (<> {resErrs.map((el, elIdx) => <span key={elIdx}>{'Verificación ' + el.code + ': ' + el.err}</span>)} </>)
        alert.show(errResp, {type: 'error', timeout: 25000});
      }
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    });
  }, [alert, data]);

  const GenerateVerificationsPreview = useCallback((records, idx) => {

    setLoading(true);

    const opts = { cancelToken: source.token };
    const url = '/api/intVerExports/fixedScale/preview';
    
    Axios.post(url, { records }, opts)
    .then((res) => {
      setLoading(false);
      if (idx || idx === 0) {
        let newRecords = [...data];
        newRecords[idx] = res.data[0];
        setData(newRecords);
      } else {
        setData(res.data);
      }
      let resErrs = res.data.filter(r => r.err);
      if (resErrs.length) {
        let errResp = (<> {resErrs.map((el, elIdx) => <span key={elIdx}>{'Informe N°' + el.reportNumber + ': ' + el.err}</span>)} </>)
        alert.show(errResp, {type: 'error', timeout: 25000});
      }
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    })
  }, [alert, data]);

  const ExportVerificationStepOne = (idx) => {
    if (data[idx].status === 'finalizado') {
      confirmAlert({
        closeOnEscape: false,
        closeOnClickOutside: false,
        customUI: ({ onClose }) => {
          return (
            <div className="modal-large-confirm-container">
              <h3>Exportar Verificación Intermedia</h3>
              <hr />
              <p>{'Esta acción debiera realizarse solo una vez. Continúe en caso de estar seguro(a) que la verificación está completa.'}</p>
              <hr />
              <div className="d-flex flex-column p-2 maxh-45vh overflow-auto fs-7">
                <p> Verificaciones: </p>
                <div className="mt-1">{(data[idx].code + ' - ' + dayjs(data[idx].date.split('T')[0]).format('DD/MM/YYYY'))}</div>
              </div>
              <div className="modal-delete-confirm-buttons-container">
                <button className="modal-confirm-button round-button silver-black me-2" onClick={() => onClose()}>
                  Cancelar
                </button>
                <button className="modal-confirm-button round-button lb-white me-2" onClick={() => { onClose(); ExportReportStepTwo(idx) }}>
                  Exportar
                </button>
              </div>
            </div>
          );
        }
      });
    } else {
      alert.show('La verificación no esta finalizada', {type: 'error', timeout: 10000});
    }
  }

  const ExportReportStepTwo = (idx) => {

    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = { cancelToken: source.token };
    const url = '/api/intVerExports/fixedScale/export';

    let records = [{ code: data[idx].code, date: data[idx].date }];

    Axios.post(url, {records}, opts)
    .then((res) => {
      setLoading(false);
      const doneReports = res.data.filter(r => !r.err).length;
      if (doneReports) {
        alert.show(('Se exportaron '+doneReports+' de ' + res.data.length + ' verificaciones con éxito'), {type: 'success', timeout: 10000});
      }
      const resErrs = res.data.filter(r => r.err);
      if (resErrs.length) {
        let errResp = <> {resErrs.map((el, elIdx) => <span key={elIdx}>{'Verificación ' + el.code + ' - ' + el.date + ': ' + el.err}</span>)} </>
        alert.show(errResp, {type: 'error', timeout: 25000});
      }
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    });
  }

  const DownloadReportFile = (idx) => {
    
    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = {
      headers: {
        'Content-Disposition': 'attachment'
      },
      responseType: 'blob',
      cancelToken: source.token
    };

    const url = '/api/downloads/intVer/fixedScale/single/'+data[idx].code+'&'+data[idx].date;

    Axios.get(url, opts)
    .then((res) => {      
      const downloadTitle = data[idx].code + ' - ' + dayjs(data[idx].date.split('T')[0]).format('DD-MM-YYYY') + '.pdf';
      FileDownload(res.data, downloadTitle);
      setLoading(false);
      alert.show('Se ha descargado su verificación con éxito', {type: 'success'})
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else if (err.response.status === 404) {
        alert.show('No se pudo descargar el archivo', {type: 'error'})
      } else if (err.response.status === 400) {
        alert.show('No se encontró el archivo', {type: 'error'})
      } else {
        alert.show('Ocurrió un error al realizar esta operación', {type: 'error'})
      }
    });
  }

  const RenderReportOpts = () => {
    return (
      <div className="d-flex position-sticky top-0 start-0 user-select-none zIndex-3 bg-secondary">
        <div className="d-flex flex-1 justify-content-center align-items-center flex-wrap">
          <div className="d-flex align-items-center">
            <Button className="d-flex p-1 rounded-circle" variant="secondary" disabled={loading || (pageScale <= 0)} 
              title="Alejar" onClick={() => setPageScale(pageScale-1)}
            >
              <BsDash color="white" size={22} className="icon-style"/>
            </Button>
            <div className="color-white"> {availablePageScales[pageScale].label + '%'} </div>
            <Button className="d-flex p-1 rounded-circle" variant="secondary" disabled={loading || (pageScale >= (availablePageScales.length-1))} 
              title="Acercar" onClick={() => setPageScale(pageScale+1)}
            >
              <BsPlus color="white" size={22} className="icon-style"/>
            </Button>
          </div>
          <div className="d-flex align-items-center ms-md-2 gap-md-3 gap-2">
            <Button className="py-1 px-2 fs-inherit" size='sm' variant='secondary' disabled={loading} 
              title="Recargar ensayo" onClick={() => LoadVerificationsSwitch([{code: data[currentDataIdx].code, date: data[currentDataIdx].date}], currentDataIdx)}
            >
              <FaSyncAlt color="white" size={15} className="icon-style me-1"/>
              <span className="fs-8">Recargar</span>
            </Button>
            <Button className="py-1 px-2 fs-inherit" size='sm' variant='secondary' disabled={(data[currentDataIdx].status !== 'finalizado') || loading} 
              title="Generar informe cliente" onClick={() => ExportVerificationStepOne(currentDataIdx)}
            >
              <FaFileExport color="white" size={15} className="icon-style me-1"/>
              <span className="fs-8">Exportar Cliente</span>
            </Button>
            <Button className="py-1 px-2 fs-inherit" size='sm' variant='secondary' disabled={(scalePreviewInfoSrc !== 2) || loading} 
              title="Descargar" onClick={() => DownloadReportFile(currentDataIdx)}
            >
              <FaCloudDownloadAlt color="white" size={15} className="icon-style me-1"/>
              <span className="fs-8">Descargar</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const LoadVerificationsSwitch = useCallback((previewInfo, idx) => {
    if (scalePreviewInfoSrc === 1) {
      GenerateVerificationsPreview(previewInfo, idx);
    } else {
      GetGeneratedVerification(previewInfo, idx);
    }
  }, [scalePreviewInfoSrc, GenerateVerificationsPreview, GetGeneratedVerification]);

  useEffect(() => {
    if (isFirstRender.current) {
      if (scalePreviewInfo?.length && scalePreviewInfoSrc) {
        LoadVerificationsSwitch(scalePreviewInfo, null);
      }
    }
  }, [scalePreviewInfo, scalePreviewInfoSrc, LoadVerificationsSwitch]);

  useEffect(() => { isFirstRender.current = false }, []);

  return (
    <div className="modal-outer-container zIndex-3">
      <LoadingModal loading={loading}/>
      <div className="modal-inner-container-large">
        <ModalHeader title={ 'Verificación Intermedia - ' + (scalePreviewInfoSrc === 1 ? 'Vista Previa' : 'Archivo generado')} closeModal={closeModal} closeKb={!loading} />
        <ModalItemSelection 
          canChange={!loading} data={scalePreviewInfo} currentItem={currentDataIdx} setItem={(value) => { setNumPages(null); setCurrentDataIdx(Number(value)) }} 
          optClasses={(item, itemIdx) => ""} optText={(opt) => (opt.code + ' - ' + (opt.date ? dayjs(opt.date.split('T')[0]).format('DD/MM/YYYY') : ''))} 
          backBtnText={'(A) Anterior'} fwdBtnText={'(S) Siguiente'} 
        />
        <div className="modal-pdf-outer-container">
          { data.length ? RenderReportOpts() : null }
          { (data.length && data[currentDataIdx]?.file) ? 
            <Document
              file={data[currentDataIdx].file}
              onLoadSuccess={onDocumentLoadSuccess}
              className="pdf-doc-container d-flex flex-column"
              width={'100%'}
            >
              { numPages ? Array.from({length: numPages}, (_, i) => i + 1).map((page, pageIdx) => 
                <Page
                  key={pageIdx}
                  pageNumber={page}
                  scale={availablePageScales[pageScale].value}
                  className={"d-flex w-100 justify-content-center bg-aliceblue" + (pageIdx < (numPages-1) ? " mb-4" : "")}
                  style={{ left: 'unset', inset: 'unset' }}
                />
              ) : null }
            </Document>
          : 
            <div className="my-4 text-center">
              No se encontró un archivo para mostrar
            </div>
          }
        </div>
      </div>
    </div>
  )
}

export default IntVerFilePreview;